import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { apiScopes } from '@/mocks/integrations';

export interface NewApiKey {
  platform: string;
  scopes: string[];
}

interface GenerateKeyModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (data: NewApiKey) => void;
}

function randomKey() {
  const chars = 'abcdef0123456789';
  let out = '';
  for (let i = 0; i < 24; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return `lb_live_${out}`;
}

export default function GenerateKeyModal({ open, onClose, onGenerate }: GenerateKeyModalProps) {
  const [platform, setPlatform] = useState('');
  const [scopes, setScopes] = useState<string[]>(['read']);
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlatform('');
    setScopes(['read']);
    setError('');
    setGeneratedKey('');
    setCopied(false);
  }, [open]);

  const toggleScope = (scopeId: string) => {
    setScopes((prev) =>
      prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId],
    );
  };

  const handleGenerate = () => {
    if (!platform.trim()) {
      setError('Please enter a platform name.');
      return;
    }
    if (scopes.length === 0) {
      setError('Select at least one scope.');
      return;
    }
    const key = randomKey();
    setGeneratedKey(key);
    setError('');
    onGenerate({ platform: platform.trim(), scopes });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const inputClasses =
    'w-full bg-background-100/70 border border-background-200/90 rounded-xl px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:bg-background-50 focus:ring-2 focus:ring-primary-500/15 shadow-2xs transition-all';

  return (
    <Modal
      open={open}
      title={generatedKey ? 'API Key Created' : 'Generate API Key'}
      subtitle={
        generatedKey
        // ? 'Make sure to copy your API secret now. You won’t be able to see it again!'
        // : 'Create a scoped API secret for your connected platform or service.'
      }
      onClose={onClose}
    >
      {generatedKey ? (
        <div className="flex flex-col gap-5 animate-fade-in">
          <div className="rounded-2xl bg-accent-50 border border-accent-200/80 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-accent-800">
                Key for <span className="underline">{platform}</span> · {scopes.length} scope
                {scopes.length > 1 ? 's' : ''}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent-200/70 text-accent-900">
                Live Secret
              </span>
            </div>

            <div className="flex items-center gap-2">
              <code className="flex-1 bg-background-50 border border-accent-300/80 rounded-xl px-3 py-2 text-xs md:text-sm font-mono text-foreground-950 font-bold break-all shadow-inner">
                {generatedKey}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-2xs cursor-pointer ${copied
                    ? 'bg-accent-600 text-white'
                    : 'bg-primary-500 hover:bg-primary-600 text-white'
                  }`}
              >
                <i className={copied ? 'ri-check-line text-sm' : 'ri-file-copy-line text-sm'}></i>
                <span>{copied ? 'Copied!' : 'Copy Key'}</span>
              </button>
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background-100/70 border border-background-200/80 text-xs text-foreground-600 flex items-start gap-2.5">
            <i className="ri-shield-keyhole-line text-primary-500 text-base shrink-0 mt-0.5"></i>
            <p className="leading-relaxed">
              Store this token safely in your environment variables. It has full permissions for the
              selected scopes.
            </p>
          </div>

          <div className="flex items-center justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap px-5 py-2 rounded-xl bg-primary-500 text-white text-xs font-bold hover:bg-primary-600 shadow-md shadow-primary-500/20 transition-all cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="key-platform" className="text-xs font-bold text-foreground-800">
              Platform Name
            </label>
            <input
              id="key-platform"
              type="text"
              value={platform}
              onChange={(event) => setPlatform(event.target.value)}
              placeholder="e.g. Shopify Store, Mobile App, POS Backend"
              className={inputClasses}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold text-foreground-800">Access Scopes</span>
            <div className="flex flex-col gap-2">
              {apiScopes.map((scope) => {
                const checked = scopes.includes(scope.id);
                return (
                  <label
                    key={scope.id}
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all ${checked
                        ? 'border-primary-300 bg-primary-50/70 shadow-2xs'
                        : 'border-background-200/90 bg-background-50 hover:bg-background-100/70'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleScope(scope.id)}
                      className="mt-0.5 w-4 h-4 rounded text-primary-600 border-background-300 focus:ring-primary-500/20 cursor-pointer"
                    />
                    <span className="flex flex-col">
                      <span className="text-xs font-bold text-foreground-900">{scope.label}</span>
                      <span className="text-[11px] text-foreground-500 mt-0.5">{scope.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3.5 py-2 animate-shake">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-background-200/80">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap px-4 py-2 rounded-xl bg-background-100 hover:bg-background-200 border border-background-200 text-xs font-bold text-foreground-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              className="whitespace-nowrap px-4.5 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xs font-bold shadow-md shadow-primary-500/25 hover:from-primary-600 hover:to-primary-700 transition-all cursor-pointer"
            >
              Generate Key
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
